import { Injectable } from '@angular/core';
import { Addon } from '@galaxy/marketplace-apps/v1';
import { Observable, combineLatest, of as observableOf, throwError as observableThrowError, of } from 'rxjs';
// TODO: this is bad, do better - export these from core
import {
  PackageService as CorePackageService,
  GetMultiAddonsRequest,
  ListPackagesRequest,
  MarketplacePackagesApiService,
  Package,
  Status,
} from '@vendasta/marketplace-packages';
import { catchError, map, shareReplay, switchMap } from 'rxjs';
import { AccountGroupService } from '../account-group/account-group.service';
import { ProductService } from './product.service';

@Injectable({ providedIn: 'root' })
export class PackageService {
  packages$ = this.agService.currentAccountGroup$.pipe(
    switchMap((accountGroup) => {
      return this.packagesApiService.listPackages(
        new ListPackagesRequest({
          marketId: accountGroup.marketId,
          partnerId: accountGroup.partnerId,
          lmiCategory: 0,
          statuses: [Status.PUBLISHED],
          pageSize: 1000,
          sort: true,
          cursor: 'MA==', // Gross :(
        }),
      );
    }),
    map((resp) => resp.packages),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    private packagesApiService: MarketplacePackagesApiService,
    private agService: AccountGroupService,
    private productService: ProductService,
    private packageService: CorePackageService,
  ) {}

  loadAddonsForPackage(pkg: Package): Observable<Addon[]> {
    if (!pkg || !pkg.addonKeys || pkg.addonKeys.length <= 0) {
      return observableOf(null);
    }
    const req = new GetMultiAddonsRequest({ addonKeys: pkg.addonKeys });
    return this.productService.activeProducts$.pipe(
      switchMap((products) => {
        return this.packagesApiService.getMultiAddons(req).pipe(
          map((resp) => {
            const addons = <Addon[]>resp['addons'] || [];
            const addonsWithProductInfo = addons.map((addon: Addon) => {
              addon = Addon.fromApi(addon);
              if (
                addon.endUserMarketing.faqs &&
                addon.endUserMarketing.faqs.length > 0 &&
                !addon.endUserMarketing.faqs[0].question
              ) {
                addon.endUserMarketing.faqs = [];
              }
              const product = products.find((p) => p.productId === addon.appId);
              if (product) {
                addon.productIcon = product.iconUrl;
                addon.productName = product.name;
              }
              return addon;
            });
            return addonsWithProductInfo;
          }),
        );
      }),
      catchError((err) => {
        err.message = 'Failed to get addons > ' + err.message;
        return observableThrowError(err);
      }),
    );
  }

  listByLmiCategories(lmiCategories: number[]): Observable<Package[]> {
    return combineLatest([this.productService.marketplaceProducts$, this.packages$]).pipe(
      map(([products, packages]) => {
        if (!lmiCategories || lmiCategories.length < 1) {
          return packages;
        }
        return packages.filter((pack) => {
          return pack?.products?.some((productId) => {
            return products
              .find((product) => product.productId === productId)
              ?.lmiCategories.some((id) => lmiCategories.indexOf(id) > -1);
          }, false);
        });
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
  packageWithId(id: string): Observable<Package> {
    return this.packages$.pipe(map((pkgs) => pkgs.find((pkg) => pkg.packageId === id)));
  }

  /* Get currently returns a superset of data that list does not return */
  get(id: string): Observable<Package> {
    return this.packageService.getMulti([id]).pipe(
      switchMap((packages) => {
        if (!packages || packages.length === 0) {
          return this.packageWithId(id);
        }
        return observableOf(packages[0]);
      }),
      catchError((err) => {
        err.message = 'Failed to get updated package with id: ' + id + ' with error > ' + err.message;
        return this.packageWithId(id);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  getMulti(ids: string[]): Observable<Package[]> {
    if (!ids || ids.length === 0) {
      return of([] as Package[]);
    }

    return this.packageService.getMulti(ids).pipe(
      catchError((err) => {
        err.message = 'Failed to get packages with error > ' + err.message;
        return of(undefined);
      }),
      map((packages) => {
        return packages && packages.filter((pkg) => pkg.status === Status.PUBLISHED);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
